import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

export const CASH_SHIFT_CASH_MOVEMENT_CONTRACT_VERSION = 1 as const;
export const CASH_SHIFT_CASH_MOVEMENT_CAPACITY = 500;

const IdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const DateSchema = Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" });
const DateTimeSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\\.[0-9]{3}Z$",
});
const NonNegativeFenSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const PositiveFenSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const SignedFenSchema = Type.Integer({
  minimum: -Number.MAX_SAFE_INTEGER,
  maximum: Number.MAX_SAFE_INTEGER,
});

export const CashShiftCashMovementNumberSchema = Type.String({
  pattern: "^XJ-[0-9]{8}-[0-9A-F]{12}$",
});
export type CashShiftCashMovementNumber = `XJ-${string}-${string}`;

export const CashShiftCashMovementDirectionSchema = Type.Union([
  Type.Literal("IN"),
  Type.Literal("OUT"),
]);
export type CashShiftCashMovementDirection = Static<typeof CashShiftCashMovementDirectionSchema>;

export const RecordCashShiftCashMovementCommandSchema = Type.Object(
  {
    expectedShiftVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    direction: CashShiftCashMovementDirectionSchema,
    amountFen: PositiveFenSchema,
    purpose: Type.String({ minLength: 2, maxLength: 200 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type RecordCashShiftCashMovementCommand = Static<
  typeof RecordCashShiftCashMovementCommandSchema
>;

export const CashShiftCashMovementRecordSchema = Type.Object(
  {
    contractVersion: Type.Literal(CASH_SHIFT_CASH_MOVEMENT_CONTRACT_VERSION),
    movementNumber: CashShiftCashMovementNumberSchema,
    shiftId: IdSchema,
    businessDate: DateSchema,
    shiftVersion: Type.Integer({ minimum: 2, maximum: Number.MAX_SAFE_INTEGER }),
    direction: CashShiftCashMovementDirectionSchema,
    amountFen: PositiveFenSchema,
    purpose: Type.String({ minLength: 2, maxLength: 200 }),
    recordedByDisplayName: Type.String({ minLength: 1, maxLength: 120 }),
    recordedAt: DateTimeSchema,
    openingCashFen: NonNegativeFenSchema,
    cashReceiptsFen: NonNegativeFenSchema,
    cashRefundsFen: NonNegativeFenSchema,
    cashInFen: NonNegativeFenSchema,
    cashOutFen: NonNegativeFenSchema,
    expectedCashFen: SignedFenSchema,
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type CashShiftCashMovementRecord = Static<typeof CashShiftCashMovementRecordSchema>;

export const CashShiftCashMovementListReadModelSchema = Type.Object(
  {
    contractVersion: Type.Literal(CASH_SHIFT_CASH_MOVEMENT_CONTRACT_VERSION),
    tenantId: IdSchema,
    storeId: IdSchema,
    shiftId: IdSchema,
    businessDate: DateSchema,
    shiftVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    openingCashFen: NonNegativeFenSchema,
    cashReceiptsFen: NonNegativeFenSchema,
    cashRefundsFen: NonNegativeFenSchema,
    cashInFen: NonNegativeFenSchema,
    cashOutFen: NonNegativeFenSchema,
    expectedCashFen: SignedFenSchema,
    movements: Type.Array(CashShiftCashMovementRecordSchema, {
      maxItems: CASH_SHIFT_CASH_MOVEMENT_CAPACITY,
    }),
    generatedAt: DateTimeSchema,
  },
  { additionalProperties: false },
);
export type CashShiftCashMovementListReadModel = Static<
  typeof CashShiftCashMovementListReadModelSchema
>;

const FNV_1A_64_OFFSET = 0xcbf29ce484222325n;
const FNV_1A_64_PRIME = 0x100000001b3n;
const LOWER_48_BITS = 0xffffffffffffn;

function canonicalDate(value: string): boolean {
  const parsed = Date.parse(`${value}T00:00:00.000Z`);
  return (
    /^\d{4}-\d{2}-\d{2}$/u.test(value) &&
    Number.isFinite(parsed) &&
    new Date(parsed).toISOString().slice(0, 10) === value
  );
}

function canonicalDateTime(value: string): boolean {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) && new Date(parsed).toISOString() === value;
}

function lower48BitHex(value: string): string {
  let hash = FNV_1A_64_OFFSET;
  for (const character of value) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * FNV_1A_64_PRIME);
  }
  return (hash & LOWER_48_BITS).toString(16).toUpperCase().padStart(12, "0");
}

export function deriveCashShiftCashMovementNumber(
  businessDate: string,
  movementId: string,
): CashShiftCashMovementNumber {
  if (!canonicalDate(businessDate)) {
    throw new TypeError("businessDate must be a real calendar date in YYYY-MM-DD format");
  }
  if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$/u.test(movementId)) {
    throw new TypeError("movementId must contain at least 8 safe characters");
  }
  return `XJ-${businessDate.replaceAll("-", "")}-${lower48BitHex(
    `XJ\0${businessDate}\0${movementId}`,
  )}`;
}

function moneyTotalsAreConsistent(value: {
  openingCashFen: number;
  cashReceiptsFen: number;
  cashRefundsFen: number;
  cashInFen: number;
  cashOutFen: number;
  expectedCashFen: number;
}): boolean {
  const first = value.openingCashFen + value.cashReceiptsFen;
  const second = first + value.cashInFen;
  const third = second - value.cashRefundsFen;
  const expected = third - value.cashOutFen;
  return (
    [first, second, third, expected].every(Number.isSafeInteger) &&
    value.expectedCashFen === expected
  );
}

export function isCashShiftCashMovementRecord(
  value: unknown,
): value is CashShiftCashMovementRecord {
  if (!Value.Check(CashShiftCashMovementRecordSchema, value)) return false;
  const record = value as CashShiftCashMovementRecord;
  return (
    canonicalDate(record.businessDate) &&
    canonicalDateTime(record.recordedAt) &&
    record.movementNumber.slice(3, 11) === record.businessDate.replaceAll("-", "") &&
    (record.direction === "IN"
      ? record.cashInFen >= record.amountFen
      : record.cashOutFen >= record.amountFen) &&
    moneyTotalsAreConsistent(record)
  );
}

export function isCashShiftCashMovementListReadModel(
  value: unknown,
): value is CashShiftCashMovementListReadModel {
  if (!Value.Check(CashShiftCashMovementListReadModelSchema, value)) return false;
  const list = value as CashShiftCashMovementListReadModel;
  if (!canonicalDate(list.businessDate) || !canonicalDateTime(list.generatedAt)) return false;
  if (!moneyTotalsAreConsistent(list)) return false;
  if (
    list.movements.some(
      (movement) =>
        !isCashShiftCashMovementRecord(movement) ||
        movement.shiftId !== list.shiftId ||
        movement.businessDate !== list.businessDate ||
        movement.recordedAt > list.generatedAt ||
        movement.shiftVersion > list.shiftVersion,
    )
  ) {
    return false;
  }
  const cashInFen = list.movements.reduce(
    (total, movement) => total + (movement.direction === "IN" ? movement.amountFen : 0),
    0,
  );
  const cashOutFen = list.movements.reduce(
    (total, movement) => total + (movement.direction === "OUT" ? movement.amountFen : 0),
    0,
  );
  return (
    Number.isSafeInteger(cashInFen) &&
    Number.isSafeInteger(cashOutFen) &&
    cashInFen === list.cashInFen &&
    cashOutFen === list.cashOutFen
  );
}
